import React, { useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import Config from "../../config";
import axios from '../../utils/axios';
import { Form, Spin, Table, Input, Layout, Checkbox, Select, Upload } from "antd";
import styled from "styled-components";
import { EyeFilled } from "@ant-design/icons";
import { toggleSpinner } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import CustomModal from '../../components/Modal';
import Button from "../../components/Button";
import { numericValidator } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    indicatorDataSelector
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";

const { TextArea } = Input;
const { Option } = Select;

const getKeysArray = (objArray) => {
    const keysArray = [];
    if (objArray?.length) {
        objArray.forEach((obj) => {
            const keys = Object.keys(obj);
            if (obj[keys[0]] === 'Y') {
                keysArray.push(keys[0]);
            }
        });
        return keysArray;
    }
}

const CharityFinalIndicators = ({ className }) => {
    const baseClassName = clsx("charityFinalIndicators", className);
    const intl = useIntl();
    const [pictures, setPictures] = useState([]);
    const [indicatorsPopUpData, setIndicatorsPopUpData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [unit, setUnit] = useState('');
    const [comment, setComment] = useState('');
    const indicators = useAppSelector(indicatorDataSelector) || {};
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);

    const getColumnData = (list) => {
        if (list?.length > 0) {
            return list?.map((item, idx) => {
                const {
                    name,
                    description,
                    targetRequired,
                    startingLevelArray,
                    targetLevelArray,
                    startingLevel,
                    finalLevelArray,
                    finalLevel,
                    targetLevel,
                    status,
                    linkedObjective,
                    linkedOutcome,
                    objectiveName,
                    outcomeName,
                    ID,
                    unit,
                    initialStatus,
                    finalStatus,
                    finalRequired,
                    indicatorType,
                    finalLevelList
                } = item || {};
                return {
                    key: idx,
                    name,
                    description,
                    targetRequired,
                    targetLevel,
                    status,
                    linkedObjective,
                    linkedOutcome,
                    objectiveName,
                    outcomeName,
                    ID,
                    unit,
                    initialStatus,
                    finalStatus,
                    finalRequired,
                    indicatorType,
                    startingLevelArray,
                    targetLevelArray,
                    startingLevel,
                    finalLevelArray,
                    finalLevel,
                    finalLevelList
                };
            });
        }
        return null;
    };

    const resetAllFields = () => {
        form.setFieldsValue({
            startingLevel: undefined,
            targetLevel: undefined,
            evidenceList: undefined,
            comments: '',
            finalLevel: undefined,
            pictures: [],
            achieved: ''
        });
        setUnit(null);
        setIsModalVisible(false);
        setComment("")
    };

    const onPreview = async (file) => {
        let src = file.url;
        if (!src && file?.originFileObj instanceof Blob) {
            src = await new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('Failed to read file'));
            });
        }

        if (src) {
            const image = new Image();
            image.src = src;
            const imgWindow = window.open(src);
            imgWindow?.document.write(image.outerHTML);
        } else {
            console.error('Failed to preview image: Source is invalid or unavailable.');
        }
    };

    const renderEvidenceSection = (param) => {
        const CheckboxGroup = Checkbox.Group;
        const options = param;
        return (
            options?.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column' }}>
                    <StyledText as="p" variant="B3d" className='mainTitle'>
                        {intl.formatMessage({ id: "evidence" })}
                    </StyledText>
                    <StyledText as="p" variant="B3" className='mainTitle'>
                        {intl.formatMessage({ id: "please_confirm_that_you_can_provide_any_of_following_evidence_items" })}
                    </StyledText>
                    <div style={{ display: 'flex', margin: '5px 0', alignItems: 'center', justifyContent: 'space-between' }} className='checkboxParentContainer'>
                        <Form.Item
                            name="evidenceList"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value || value.length < options.length) {
                                            return Promise.reject(
                                                new Error(intl.formatMessage({ id: 'select_all_options' }))
                                            );
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            className='checkboxParentContainerFormItem'
                            style={{ marginBottom: '0px' }}
                        >
                            <CheckboxGroup className="checkboxContainer" style={{ display: 'flex', flexDirection: 'column' }}>
                                {options?.length > 0 &&
                                    options.map((parent) => (
                                        <Checkbox
                                            key={parent}
                                            value={parent}
                                            style={{ marginLeft: '0', fontSize: '16px', marginBottom: '10px' }}
                                        >
                                            <StyledText as="span" variant="B3">
                                                {parent}
                                            </StyledText>
                                        </Checkbox>
                                    ))
                                }
                            </CheckboxGroup>
                        </Form.Item>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {options.map((parent) => {
                                return ((
                                    <div style={{ marginBottom: '10px', display: 'flex' }} key={parent}>
                                        <Form.Item className='uploadWrapper' name="answerEvidenceRequiredList">
                                            <Upload
                                                listType="picture-card"
                                                fileList={pictures}
                                                onPreview={onPreview}
                                            >
                                                {pictures?.length < 1 && `+ ${intl.formatMessage({ id: "upload" })}`}
                                            </Upload>
                                        </Form.Item>
                                    </div>
                                ));
                            })}
                        </div>
                    </div>
                </div>
            )
        );
    };

    const getOptionalSection = (startingRequired, targetRequired, indicatorType, baseLevel) => baseLevel && (startingRequired === 'Y' || targetRequired === 'Y')
        && ['percentage', 'number']?.includes(indicatorType);

    const getRelativeChangeValue = (absoluteChange, baseLevel, startingLevel) => (absoluteChange && baseLevel
        ? absoluteChange + (startingLevel - baseLevel)
        : null);

    const getAbsoluteChangeValue = (startingLevel, targetLevel) => (startingLevel && targetLevel
        ? targetLevel - startingLevel
        : null);


    const getAddOnAfter = (unit, indicatorType) => (unit || indicatorType === 'percentage' || indicatorType === 'array-percentage' ? (
        <span>{`${unit || '%'}`}</span>
    ) : undefined);

    const getActualAbsoluteChange = (finalLevel, startingLevel) => finalLevel - startingLevel;

    const getActualRelativeChange = (finalLevel, startingLevel, baseLevel) => getActualAbsoluteChange(finalLevel, startingLevel) + (startingLevel - baseLevel);

    const countWords = (text) => {
        return text?.trim()?.split(/\s+/)?.filter(word => word.length > 0).length;
    };

    const handleTextChange = (setter, maxWords) => (e) => {
        const text = e.target.value;
        if (countWords(text) <= maxWords) {
            setter(text);
            form.setFieldsValue({ [e.target.name]: text });
        }
    };

    const wordCountSummary = countWords(comment);

    const renderModal = () => {
        const {
            name,
            description,
            indicatorType,
            startingRequired,
            targetRequired,
            predefinedList,
            startingLevel,
            targetLevel,
            baseLevel,
            approachDescription,
            startingLevelList,
            targetLevelList,
            finalLevelList,
            comment,
            answerEvidenceRequired,
            finalLevel,
        } = indicatorsPopUpData || {};

        const showOptionalSection = getOptionalSection(
            startingRequired,
            targetRequired,
            indicatorType,
            baseLevel
        );

        const absoluteChange = getAbsoluteChangeValue(startingLevel, targetLevel);
        const relativeChange = getRelativeChangeValue(absoluteChange, baseLevel, startingLevel);
        const actualAbsoluteChange = getActualAbsoluteChange(finalLevel, startingLevel);
        const actualRelativeChange = getActualRelativeChange(finalLevel, startingLevel, baseLevel);
        const useArrayFields = indicatorType === 'pre-def-multi';
        const mode = useArrayFields ? 'multiple' : undefined;
        return (
            <CustomModal
                title={intl.formatMessage({ id: 'create_indicator' })}
                open={isModalVisible}
                onCancel={() => resetAllFields()}
                className="indicatorPop"
            >
                {showSpinner ? (
                    <div className="overlay">
                        <Spin className="spinner" size="large" spinning={showSpinner} />
                    </div>
                ) :

                    <Form form={form} initialValues={{
                        baseLevel: baseLevel
                    }}>
                        <Layout className="dropDownBackgroundModal">
                            <div>
                                <StyledText as="span" variant="B3d">
                                    {intl.formatMessage({ id: "indicator_name" })}
                                    {': '}
                                </StyledText>
                                <StyledText as="span" variant="B3">
                                    {name}
                                </StyledText>
                            </div>
                            <div>
                                <StyledText as="span" variant="B3d">
                                    {intl.formatMessage({ id: "description" })}
                                    {': '}
                                </StyledText>
                                <StyledText as="span" variant="B3">
                                    {description}
                                </StyledText>
                            </div>
                            <StyledText as="span" variant="B3d">
                                {intl.formatMessage({ id: "measuring_progress" })}
                            </StyledText>
                            <div>
                                {['percentage', 'number']?.includes(indicatorType) && (
                                    <>
                                        {startingRequired === 'Y' && (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Form.Item
                                                    className="dFlex inputStyle"
                                                    label={<StyledText as="p" variant="B3">
                                                        {intl.formatMessage({
                                                            id: 'starting_number_level_of_your_indicator',
                                                        })}
                                                    </StyledText>
                                                    }
                                                    name="startingLevel"
                                                    initialValue={startingLevel}
                                                >
                                                    <Input
                                                        className="inputAlign"
                                                        placeholder={intl.formatMessage({
                                                            id: 'please_enter',
                                                        })}
                                                        addonAfter={getAddOnAfter(unit, indicatorType)}
                                                        disabled
                                                    />
                                                </Form.Item>
                                            </div>
                                        )}
                                        {targetRequired === 'Y' && (
                                            <Form.Item
                                                className="dFlex marginBottomStandard inputStyle"
                                                label={<StyledText as="p" variant="B3">
                                                    {intl.formatMessage({
                                                        id: 'target_number_or_level_to_reach',
                                                    })}
                                                </StyledText>}
                                                initialValue={targetLevel}
                                                name="targetLevel"
                                            >
                                                <Input
                                                    className="inputAlign"
                                                    placeholder={intl.formatMessage({
                                                        id: 'please_enter',
                                                    })}
                                                    addonAfter={getAddOnAfter(unit, indicatorType)}
                                                    disabled
                                                />
                                            </Form.Item>
                                        )}
                                    </>
                                )}
                                {absoluteChange && <StyledText as="p" variant="B3b" className='customModalTitle'>
                                    {intl.formatMessage({
                                        id: 'the_target_we_aim_to_reach_is_employed_from_the_starting_number_level',
                                    }).replace(
                                        'employedNumber',
                                        absoluteChange > 0
                                            ? `+${absoluteChange}`
                                            : `${absoluteChange}`
                                    )}
                                </StyledText>}
                                {(showOptionalSection) && (
                                    <div style={{ alignItems: 'flex-end' }}>
                                        {(
                                            <>
                                                {baseLevel && <Form.Item
                                                    className="dFlexTop whiteSpaceBreak marginBottomStandard optional inputStyle"
                                                    labelCol={{ span: 12 }}
                                                    wrapperCol={{ span: 12 }}
                                                    initialValue={baseLevel}
                                                    label={(
                                                        <div style={{
                                                            display: 'flex', flexDirection: 'column', textAlign: 'left', lineHeight: '18px'
                                                        }}
                                                        ><StyledText as="p" variant="B3">
                                                                {intl.formatMessage({
                                                                    id: 'optional_title',
                                                                })}:
                                                            </StyledText>
                                                            <StyledText as="p" variant="B3" className='estimateText'>
                                                                {intl.formatMessage({
                                                                    id: 'estimate_what_the_level_would_be',
                                                                })}
                                                            </StyledText>
                                                        </div>
                                                    )}
                                                    name="baseLevel"
                                                >
                                                    <Input
                                                        className="inputAlign"
                                                        placeholder={intl.formatMessage({
                                                            id: 'please_enter',
                                                        })}
                                                        value={baseLevel}
                                                        disabled
                                                    />
                                                </Form.Item>}
                                                {startingLevel && baseLevel && typeof relativeChange === 'number'
                                                    && !isNaN(relativeChange)
                                                    && typeof absoluteChange === 'number' && (
                                                        <StyledText as="p" variant="B3b" className='customModalTitle'>
                                                            {intl
                                                                .formatMessage({
                                                                    id: 'your_total_impact_will_be_employed',
                                                                })
                                                                .replace(
                                                                    'impactNumber',
                                                                    relativeChange > 0
                                                                        ? `+${relativeChange}`
                                                                        : `${relativeChange}`
                                                                )}
                                                        </StyledText>
                                                    )}
                                            </>
                                        )}
                                        {indicatorType === 'qualitative' && (
                                            <>
                                                {startingRequired === 'Y' && (
                                                    <Form.Item
                                                        className="dFlex"
                                                        label={intl.formatMessage({
                                                            id: 'describe_the_starting_situation',
                                                        })}
                                                        name="startingLevel"
                                                        initialValue={startingLevel}
                                                    >
                                                        <TextArea
                                                            className="textContent"
                                                            maxLength="300"
                                                            placeholder="please_enter"
                                                            rows={2}
                                                            disabled
                                                        />
                                                    </Form.Item>
                                                )}
                                                {targetRequired === 'Y' && (
                                                    <Form.Item
                                                        className="dFlex marginBottom"
                                                        label={intl.formatMessage({
                                                            id: 'describe_the_final_situation',
                                                        })}
                                                        name="targetLevel"
                                                        initialValue={targetLevel}
                                                    >
                                                        <TextArea
                                                            className="textContent"
                                                            maxLength="300"
                                                            placeholder="please_enter"
                                                            rows={2}
                                                            disabled
                                                        />
                                                    </Form.Item>
                                                )}
                                            </>
                                        )}
                                        {['percentage', 'number']?.includes(indicatorType) && <Form.Item
                                            className="dFlexTop whiteSpaceBreak marginBottomStandard optional inputStyle archived"
                                            labelCol={{ span: 12 }}
                                            wrapperCol={{ span: 12 }}
                                            label={(
                                                <div style={{
                                                    display: 'flex', flexDirection: 'column', textAlign: 'left', lineHeight: '18px'
                                                }}
                                                >
                                                    <StyledText as="p" variant="B3">
                                                        {intl.formatMessage({
                                                            id: 'achieved',
                                                        })}
                                                    </StyledText>
                                                </div>
                                            )}
                                            name="finalLevel"
                                            initialValue={finalLevel}
                                            rules={[
                                                {
                                                    required: false,
                                                    message: intl.formatMessage({
                                                        id: 'please_enter',
                                                    }),
                                                },
                                            ]}
                                            disabled
                                        >
                                            <Input
                                                className="inputAlign"
                                                placeholder={intl.formatMessage({
                                                    id: 'please_enter',
                                                })}
                                            />
                                        </Form.Item>}
                                        {typeof actualAbsoluteChange === 'number'
                                            && !isNaN(actualAbsoluteChange) && (
                                                <StyledText as="p" variant="B3b" className='customModalTitle'>
                                                    {intl
                                                        .formatMessage({
                                                            id: 'your_absolute_impact_is',
                                                        })
                                                        .replace(
                                                            'impactNumber',
                                                            actualAbsoluteChange > 0
                                                                ? `+${actualAbsoluteChange}`
                                                                : `${actualAbsoluteChange}`
                                                        )}
                                                </StyledText>
                                            )}
                                        {typeof actualRelativeChange === 'number'
                                            && !isNaN(actualRelativeChange) && (
                                                <StyledText as="p" variant="B3b" className='customModalTitle'>
                                                    {intl
                                                        .formatMessage({
                                                            id: 'your_relative_impact_is',
                                                        })
                                                        .replace(
                                                            'impactNumber',
                                                            actualRelativeChange > 0
                                                                ? `+${actualRelativeChange}`
                                                                : `${actualRelativeChange}`
                                                        )}
                                                </StyledText>
                                            )}
                                    </div>
                                )

                                }
                                {['pre-def-multi', 'pre-def-single']?.includes(indicatorType) && (
                                    <>
                                        {startingRequired === 'Y' && (
                                            <Form.Item
                                                className="dFlex"
                                                label={intl.formatMessage({
                                                    id: 'describe_the_starting_situation',
                                                })}
                                                name={useArrayFields ? 'startingLevelList' : 'startingLevel'}
                                                initialValue={useArrayFields
                                                    ? startingLevelList
                                                    : startingLevel}
                                            >
                                                <Select
                                                    placeholder={intl.formatMessage({ id: 'select_title' })}
                                                    className="selectBox"
                                                    mode={mode}
                                                    disabled
                                                >
                                                    {predefinedList?.length > 0
                                                        && predefinedList.map((item) => (
                                                            <Option key={item} value={item}>
                                                                {item}
                                                            </Option>
                                                        ))}
                                                </Select>
                                            </Form.Item>
                                        )}
                                        {targetRequired === 'Y' && (
                                            <Form.Item
                                                className="dFlex marginBottom"
                                                label={intl.formatMessage({
                                                    id: 'describe_the_final_situation',
                                                })}
                                                name={useArrayFields ? 'targetLevelList' : 'targetLevel'}
                                                initialValue={useArrayFields
                                                    ? targetLevelList
                                                    : targetLevel}
                                            >
                                                <Select
                                                    placeholder={intl.formatMessage({ id: 'select_title' })}
                                                    className="selectBox"
                                                    mode={mode}
                                                    disabled
                                                >
                                                    {predefinedList?.length > 0
                                                        && predefinedList.map((item) => (
                                                            <Option key={item} value={item}>
                                                                {item}
                                                            </Option>
                                                        ))}
                                                </Select>
                                            </Form.Item>
                                        )}
                                        <Form.Item
                                            className="dFlex marginBottom archived"
                                            label={intl.formatMessage({
                                                id: 'achieved',
                                            })}
                                            name={useArrayFields ? 'finalLevelList' : 'finalLevel'}
                                            initialValue={useArrayFields
                                                ? finalLevelList
                                                : finalLevel}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: intl.formatMessage({
                                                        id: 'please_enter',
                                                    }),
                                                },
                                            ]}
                                        >
                                            <Select
                                                placeholder={intl.formatMessage({ id: 'select_title' })}
                                                className="selectBox"
                                                mode={mode}
                                                disabled
                                            >
                                                {predefinedList?.length > 0
                                                    && predefinedList.map((item) => (
                                                        <Option key={item} value={item}>
                                                            {item}
                                                        </Option>
                                                    ))}
                                            </Select>
                                        </Form.Item>
                                    </>
                                )}
                                {['array-percentage', 'array-number']?.includes(indicatorType) && (
                                    <>
                                        {startingRequired === 'Y' && (
                                            <>
                                                <span>
                                                    {intl.formatMessage({
                                                        id: 'describe_the_starting_situation',
                                                    })}
                                                </span>
                                                {predefinedList?.length > 0
                                                    && predefinedList.map((item) => (
                                                        <div style={{ display: 'flex' }} key={item}>
                                                            <Form.Item
                                                                key={item}
                                                                className="dFlex marginBottomStandard inputStyle"
                                                                name={`startingLevel-${item}`}
                                                            >
                                                                <Input
                                                                    placeholder={intl.formatMessage({
                                                                        id: 'enter_title',
                                                                    })}
                                                                    addonAfter={getAddOnAfter(unit, indicatorType)}
                                                                    disabled
                                                                />
                                                            </Form.Item>
                                                            <div style={{ display: 'flex', margin: '9px' }}>{item}</div>
                                                        </div>
                                                    ))}
                                            </>
                                        )}
                                        {targetRequired === 'Y' && (
                                            <>
                                                <span>
                                                    {intl.formatMessage({
                                                        id: 'describe_the_final_situation',
                                                    })}
                                                </span>
                                                {predefinedList?.length > 0
                                                    && predefinedList.map((item) => (
                                                        <div style={{ display: 'flex' }} key={item}>
                                                            <Form.Item
                                                                key={item}
                                                                className="dFlex marginBottomStandard inputStyle"
                                                                name={`targetLevel-${item}`}
                                                            >
                                                                <Input
                                                                    placeholder={intl.formatMessage({
                                                                        id: 'enter_title',
                                                                    })}
                                                                    disabled
                                                                    addonAfter={getAddOnAfter(unit, indicatorType)}
                                                                />
                                                            </Form.Item>
                                                            <div style={{ display: 'flex', margin: '9px' }}>{item}</div>
                                                        </div>
                                                    ))}
                                            </>
                                        )}
                                        {<>
                                            <span>
                                                {intl.formatMessage({
                                                    id: 'achieved',
                                                })}:
                                            </span>
                                            {predefinedList?.length > 0
                                                && predefinedList.map((item) => (
                                                    <div style={{ display: 'flex' }} key={item}>
                                                        <Form.Item
                                                            key={item}
                                                            className="dFlex marginBottomStandard inputStyle"
                                                            name={`finalLevel-${item}`}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: intl.formatMessage({
                                                                        id: indicatorType === 'array-number' ? 'please_enter_number' : 'please_enter',
                                                                    }),
                                                                },
                                                                { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                                            ]}
                                                        >
                                                            <Input
                                                                placeholder={intl.formatMessage({
                                                                    id: 'enter_title',
                                                                })}
                                                                addonAfter={getAddOnAfter(unit, indicatorType)}
                                                                disabled
                                                            />
                                                        </Form.Item>
                                                        <div style={{ display: 'flex', margin: '9px' }}>{item}</div>
                                                    </div>
                                                ))}
                                        </>}
                                    </>
                                )}
                                {renderEvidenceSection(answerEvidenceRequired)}
                            </div>
                            <StyledText as="p" variant="B3d">
                                {intl.formatMessage({ id: "how_will_you_gather_evidence" })}
                            </StyledText>
                            <StyledText as="p" variant="B3">
                                {approachDescription}
                            </StyledText>

                            <StyledText as="span" variant="B3d">
                                {intl.formatMessage({ id: "comments" })}
                                <StyledText as="span" variant="B3a">
                                    {` (${intl.formatMessage({ id: "optional" })})`}
                                </StyledText>
                            </StyledText>
                            <div className='textAreaIndicators'>
                                <TextArea
                                    value={comment}
                                    style={{ width: '530px' }}
                                    rows={2}
                                    placeholder={intl.formatMessage({ id: "enter_description" })}
                                    onChange={handleTextChange(setComment, 80)}
                                    disabled
                                />
                                <p className={`wordCountModal ${wordCountSummary >= 80 ? 'error' : ''}`}>
                                    {wordCountSummary}/{80} words
                                </p>
                            </div>
                        </Layout>
                    </Form>
                }
            </CustomModal>
        );
    };

    const transformObject = (inputObj, check) => Object.keys(inputObj).reduce((result, key) => {
        const newKey = `${check}-${key}`;
        result[newKey] = inputObj[key];
        return result;
    }, {});

    const handleModalResponse = (res) => {
        if (res?.data?.indicator) {
            setIndicatorsPopUpData([])
            dispatch(toggleSpinner(false));
            setIndicatorsPopUpData(res.data.indicator);
            if (
                (res?.data?.indicator?.indicatorType === 'array-percentage'
                    || res?.data?.indicator?.indicatorType === 'array-number')
                && res?.data?.indicator?.startingLevelArray
                && res?.data?.indicator?.targetLevelArray
            ) {
                const staringLevel = transformObject(
                    res?.data?.indicator?.startingLevelArray,
                    'startingLevel'
                );
                const targetLevel = transformObject(
                    res?.data?.indicator?.targetLevelArray,
                    'targetLevel'
                );
                const finalLevel = transformObject(
                    res?.data?.indicator?.finalLevelArray,
                    'finalLevel'
                ) || [];

                const mergedArray = { ...staringLevel, ...targetLevel, ...finalLevel };
                form.setFieldsValue(mergedArray);
            }
            const keysArray = getKeysArray(res?.data?.indicator?.answerEvidenceRequiredList) || [];
            if (keysArray?.length > 0) {
                form.setFieldsValue({
                    evidenceList: keysArray,
                });
            }
            form.setFieldsValue({
                comment: res?.data?.indicator?.comment,
                startingLevel: res?.data?.indicator?.startingLevel,
                targetLevel: res?.data?.indicator?.targetLevel
            });
            setComment(res?.data?.indicator?.comment)
            setTimeout(() => {
                setIsModalVisible(true);
            }, 500);
        }
    };

    const handleStatusClick = (report) => {
        resetAllFields();
        dispatch(toggleSpinner(true));
        setUnit(unit)
        const indicatorUrl = `${Config.dev.url.getEditIndicatorAnswers}?indicatorAnswerID=${report?.ID}`;
        axios.get(indicatorUrl, handleModalResponse);
    };

    const generalIndicatorsColumns = [
        {
            title: intl.formatMessage({ id: 'name' }),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: intl.formatMessage({ id: 'starting' }),
            dataIndex: 'startingLevel',
            key: 'startingLevel',
            render: (text, report) => {
                return (report?.indicatorType === "array-number" || report?.indicatorType === "array-percentage") && report?.startingLevelArray ? (
                    Object.entries(report?.startingLevelArray)?.map(([key, value]) => <div key={key}
                        className="statusButton"
                    ><span className='outputNumber'>{value}</span> of {key}
                    </div>)
                ) : (report?.indicatorType === "pre-def-multi" || report?.indicatorType === "pre-def-single")
                    ? report?.startingLevelList?.map((el) => <div className='outputNumberList'>{el}</div>) : <div>{text}</div>
            }
        },
        {
            title: intl.formatMessage({ id: 'target' }),
            dataIndex: 'targetLevel',
            key: 'targetLevel',
            render: (text, report) => {
                return (report?.indicatorType === "array-number" || report?.indicatorType === "array-percentage") && report?.targetLevelArray ? (
                    Object.entries(report?.targetLevelArray)?.map(([key, value]) => <div key={key}
                        className="statusButton"
                    ><span className='outputNumber'>{value}</span> of {key}
                    </div>)
                ) : (report?.indicatorType === "pre-def-multi" || report?.indicatorType === "pre-def-single")
                    ? report?.targetLevelList?.map((el) => <div key={el} className='outputNumber'>{el}</div>) : <div>{text}</div>
            }
        },
        {
            title: intl.formatMessage({ id: 'final' }),
            dataIndex: 'finalLevel',
            key: 'finalLevel',
            render: (text, report) => {
                const { indicatorType, finalLevelArray, finalLevelList } = report || {};
                const isArrayNumberOrPercentage = indicatorType === "array-number" || indicatorType === "array-percentage";
                if (isArrayNumberOrPercentage && finalLevelArray && Object.entries(finalLevelArray).length > 0) {
                    return Object.entries(finalLevelArray).map(([key, value]) => (
                        <div key={key} className="statusButton">
                            <span className="outputNumber">{value}</span> of {key}
                        </div>
                    ));
                }

                if (finalLevelList?.length > 0) {
                    return finalLevelList.map((el, index) => (
                        <div key={el} className="statusButton">
                            <span className="outputNumber">{el}</span>
                        </div>
                    ));
                }

                return <div>{text}</div>;
            }
        },
        {
            title: intl.formatMessage({ id: 'details' }),
            dataIndex: 'status',
            key: 'status',
            render: (text, report) => {
                return (
                    <Button
                        className="statusButton"
                        onClick={() => handleStatusClick(report)}
                    >
                        <EyeFilled />
                    </Button>
                );
            },
        },
    ];

    const oppIndicatorsColumns = [
        {
            title: intl.formatMessage({ id: 'name' }),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: intl.formatMessage({ id: 'starting' }),
            dataIndex: 'startingLevel',
            key: 'startingLevel',
            render: (text, report) => {
                return (report?.indicatorType === "array-number" || report?.indicatorType === "array-percentage") && report?.startingLevelArray ? (
                    Object.entries(report?.startingLevelArray)?.map(([key, value]) => <div key={value}
                        className="statusButton"
                    ><span className='outputNumber'>{value}</span> of {key}
                    </div>)
                ) : <div>{text}</div>
            }
        },
        {
            title: intl.formatMessage({ id: 'target' }),
            dataIndex: 'targetLevel',
            key: 'targetLevel',
            render: (text, report) => {
                return (report?.indicatorType === "array-number" || report?.indicatorType === "array-percentage") && report?.targetLevelArray ? (
                    Object.entries(report?.targetLevelArray)?.map(([key, value]) => <div key={value}
                        className="statusButton"
                    ><span className='outputNumber'>{value}</span> of {key}
                    </div>)
                ) : <div>{text}</div>
            }
        },
        {
            title: intl.formatMessage({ id: 'achieved' }),
            dataIndex: 'finalLevel',
            key: 'finalLevel',
            render: (text, report) => {
                const { indicatorType, finalLevelArray, finalLevelList } = report || {};
                const isArrayNumberOrPercentage = indicatorType === "array-number" || indicatorType === "array-percentage";
                if (isArrayNumberOrPercentage && finalLevelArray && Object.entries(finalLevelArray).length > 0) {
                    return Object.entries(finalLevelArray).map(([key, value]) => (
                        <div key={key} className="statusButton">
                            <span className="outputNumber">{value}</span> of {key}
                        </div>
                    ));
                }

                if (finalLevelList?.length > 0) {
                    return finalLevelList.map((el, index) => (
                        <div key={el} className="statusButton">
                            <span className="outputNumber">{el}</span>
                        </div>
                    ));
                }

                return <div>{text}</div>;
            }
        },
        {
            title: intl.formatMessage({ id: 'details' }),
            dataIndex: 'status',
            key: 'status',
            render: (text, report) => {
                return (
                    <Button
                        className="statusButton"
                        onClick={() => handleStatusClick(report)}
                    >
                        <EyeFilled />
                    </Button>
                );
            },
        },
    ];

    const generalIndicators = Array.isArray(indicators) && indicators?.filter(
        (item) => item.isGeneral === 'Y'
    );
    const oppIndicators = Array.isArray(indicators) && indicators?.filter((item) => item.isGeneral === 'N');

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <div className='contentContainer'>
                <div>
                    {oppIndicators?.length > 0 && <><StyledText as="p" variant="H3" className='mainTitle'>
                        {intl.formatMessage({ id: "project_indicators" })}
                    </StyledText>
                        <Table
                            className="generalIndicators"
                            columns={oppIndicatorsColumns}
                            dataSource={getColumnData(oppIndicators)}
                            pagination={false}
                        /></>}
                    {generalIndicators?.length > 0 && <>
                        <StyledText as="p" variant="H3" className='mainTitle'>
                            {intl.formatMessage({ id: "general_indicators" })}
                        </StyledText>
                        <Table
                            className="components-table-demo-nested generalIndicators"
                            columns={generalIndicatorsColumns}
                            dataSource={getColumnData(generalIndicators)}
                            pagination={false}
                        /></>}
                    {renderModal()}
                </div>
            </div>
        </div>
    )
}

const StyledCharityFinalIndicators = styled(CharityFinalIndicators)`
&.charityFinalIndicators{
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .mainTitle{
        margin-bottom: 20px;
    }

    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .generalIndicators{
        margin-bottom: 40px;
    }
    .overlay {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1000;
            height: 100%;
            background-color: rgba(255, 255, 255, 0.45);
        }
    .spinner {
            z-index: 2000;
            margin: auto;
            left: 50%;
            right: 50%;
            position: absolute;
            bottom: 50%;
    }
    .statusButton{
    background: transparent in !important;
    border: 0 !important;
}
    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -100px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .tipsContainer{
    background: #D7FEFB;
    border: 1px solid #04ac9c;
    padding: 20px;
    border-radius: 10px;
    width: max-content;
    height: max-content;
    display: flex;
    flex-direction: column;
    gap: 10px;
    .titleContainer {
        display: flex;
        margin-bottom: 20px;
        align-items: center;
        gap: 10px;
    }
  }
  .companyDonationDelete{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
  p {
    color: white !important;
  }
}

.companyDonationDelete:hover{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
  p {
    color: white !important;
  }
}

.editButton{
    background: #04ac9c !important;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
  p {
    color: white !important;
  }
}

.companyDonationDelete:focus{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
  p {
    color: white !important;
  }
}
.companyDonationButton {
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
  p {
    color: white !important;
  }
}

.companyDonationButton:hover{
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
  p {
    color: white !important;
  }
}

.companyDonationButton:focus{
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
  p {
    color: white !important;
  }
}
.oppTheme {
  padding: 20px 0px 0 50px;
  width: 100%;
  display: inline-block;
  background: none;
  top: 0;
  position: relative;
}
.rightContainer {
  width: 50px;
}
.blueTag{
  color: #5271ff;
  background: #eef1ff;
  border-color: #5271ff;
}
.dummyContainer {
  width: 50px;
  height: 50px;
}

.companyDonationDelete{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
}

.downloadBtn {
  color: #5271FF !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  font-weight: bold !important;
  cursor: pointer;
}

.companyDonationDelete:hover{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
}
.checkboxParentContainerFormItem.ant-form-item{
        margin-bottom: 0 !important;
    }

.checkboxParentContainer{
    justify-content: space-between;
    .checkboxContainer{
        display: flex !important;
        flex-direction: column !important;
    }
}

.companyDonationDelete:focus{
  background: #ff1616;
  font-weight: bold;
  color: #ffffff;
  border: 1px solid;
}

.dropDownBackground{
  background: none;
  display: flex;
  flex-direction: column;
}

.buttonContainer{
  display: flex;
  justify-content: space-between;
  background: white;
}

.fontBold{
  color: rgba(0, 0, 0, 0.65);
  font-weight: bold;
}

.marginBottom{
  margin-bottom: 24px !important;
  display: inline-block;
}

.optional ::after{
  display: none;
}

.marginLeft{
  margin-left: 15px !important;
}

.marginBottomStandard{
  margin-bottom: 12px !important;
  display: inline-block;
}

.delete{
  background: red;
}

.complete{
background: green;
}

.dFlex{
  display: flex;
  font-weight: 400;
}

.dFlexTop {
  display: flex;
  font-weight: 400;
  align-items: flex-start !important;
}

.whiteSpaceBreak {
  white-space: break-spaces;
  align-items: center;
}

.whiteSpaceBreak label{
  white-space: break-spaces;
}

.companyDonationButton {
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
}

.inputStyle input {
  width: 90px !important;
}

.inputAlign {
  /* margin-left: 100px !important; */
}

.editButtonTile{
  display: inline-block !important;
  white-space: nowrap !important;
}

.companyDonationButton:hover{
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
}

.companyDonationButton:focus{
  background: #5271FF;
  color: #ffffff;
  font-weight: bold;
  border: 1px solid #5271FF;
}

.darkGraytext {
  color: rgba(0, 0, 0, 0.65);
  font-weight: 700;
  font-size: 16px;
}

.standardText {
  font-size: 16px;
}

.saveExit{
  color: #00aa9d !important;
}

.selectBox {
  width: 150px !important;
}

.helperText {
  color: #5271FF !important;
  font-weight: bold !important;
  display: flex;
  margin-top: 10px;
}
.coverImageContainer {
  width: 100%;
  height: auto;
  min-height: 80px;
}

.imgSet {
  max-width: 100%;
  display: block;
}

.oppTheme > div > label {
  width: 100%;
  margin-bottom: 6px;
}
.themesMain {
  display: flex;
  flex-direction: row;
  margin-left: 24px;
}
.bizgiveTheme {
  display: flex;
}
.themesParent {
  background-color: #f9fafb;
  width: 10%;
}
.themeSelectedContainerSet {
  width: 30%;
  top: -3px;
  position: relative;
  padding-left: 10px;
}
.oppTheme > section:nth-last-child(2) {
  margin: 10px 0;
}
.radioStyle {
  display: block;
  line-height: 20px;
}
.hintText {
  display: block;
  font-size: 11px;
}
.oppTheme .radioButtons {
  margin-top: 10px;
  margin-bottom: 5px;
}
.themecolumns {
  width: 60%;
  margin-left: 0;
  margin-top: 10px;
}
.themecolumns > section > section > div {
  margin-bottom: 10px;
}
.mandatory::after {
  display: inline-block;
  margin-right: 4px;
  color: #f5222d;
  font-size: 14px;
  line-height: 1;
  content: "*";
}

.tipsLink {
  color: #04ac9c;
  text-decoration: underline;
}
.inputFormField {
  margin-top: 10px;
}
.inputFormField > div:nth-child(2) {
  width: 50%;
}
.oprtnitySupport > div:first-child {
  margin-bottom: 10px;
}
.oprtnitySupport > section {
  margin-bottom: 20px;
}
.oprtnitySupport > section > div:nth-child(2) {
  width: 30%;
  margin-bottom: 0;
}
.accountDetails {
  width: 100%;
}
.accountDetails > div {
  margin-bottom: 0;
  display: inline-block;
}
.accountDetails > div > div:nth-child(1) {
  float: left;
  width: auto;
  min-width: 70px;
  text-align: left;
}
.accountDetails > div > div:nth-child(2) {
  float: left;
  margin-bottom: 0;
}
.redText {
  color: red;
  margin-top: 15px;
  margin-bottom: 16px;
}
.localSecondaryTHolder > div {
  margin-left: 5px;
}
.localSecondaryTHolder > div > section > article {
  right: 50px !important;
}
.summaryObjContainer {
  border-bottom: 1px solid #ccc;
  padding-bottom: 25px;
  width: 98%;
  margin-bottom: 25px;
}
.oppTheme > section {
  margin-left: 0px !important;
}
.hintHeight {
  margin-bottom: 25px;
}
.spanSpace::after {
  content: "\A\A";
  white-space: pre;
}
.boldFont {
  font-weight: 800;
}
.opportunityThemes {
  display: block;
}
.adjustmentOne {
  margin-bottom: -5px;
}
.adjustmentTwo {
  margin-top: 15px;
}
.themesContainer {
  width: 100%;
  display: inline-block !important;
}
.themesContainer > div:nth-child(1) {
  float: left;
  width: 100%;
  margin-bottom: 0;
}
.themesContainer > div:nth-child(2) {
  float: left;
  width: 65%;
  margin-bottom: 0;
}
.themesContainer .formItem .value {
  line-height: 20px;
  font-size: 14px;
}
.themesContainer > section {
  float: left;
  margin-bottom: 10px;
  position: relative;
  line-height: 20px;
  display: inline-block;
}
.themesContainer > section:nth-child(1) {
  width: 35%;
}
.themesContainer > section:nth-child(2) {
  width: 60%;
}
.bizGiveThemes {
  width: 100%;
  padding-bottom: 1%;
}
.bizGivePrimaryThemes {
  width: 51% !important;
}
.bizGiveSecondaryThemes {
  width: 49% !important;
}
.localNoSecondary {
  min-height: 50px !important;
  padding-top: 3px !important;
}
}`;

export default StyledCharityFinalIndicators;